import { useRef, useEffect } from 'react';

interface ColumnSelectAllCheckboxProps {
  /** Number of cells in the column that have a proposed value. */
  total: number;
  /** How many of those proposed cells are currently checked. */
  checkedCount: number;
  /** Toggle every proposed cell in the column on (true) or off (false). */
  onToggle: (value: boolean) => void;
  disabled?: boolean;
  /** Accessible label, e.g. the column's field name. */
  label?: string;
}

/**
 * Tri-state header checkbox for a bulk-enrich review-grid column.
 *
 * Reflects the column's proposed cells: checked when all are selected,
 * unchecked when none are, indeterminate when mixed. Clicking checks all
 * unless every proposed cell is already checked, in which case it unchecks
 * all (indeterminate is treated as "not all checked" → checks all).
 *
 * Matches the per-cell checkbox styling used in the grids.
 */
export function ColumnSelectAllCheckbox({
  total,
  checkedCount,
  onToggle,
  disabled = false,
  label,
}: ColumnSelectAllCheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);

  const allChecked = total > 0 && checkedCount === total;
  const indeterminate = checkedCount > 0 && checkedCount < total;

  // `indeterminate` is a DOM-only property, not a React attribute.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className="mt-0.5 shrink-0 cursor-pointer align-middle"
      checked={allChecked}
      disabled={disabled || total === 0}
      title={total === 0 ? 'No proposals in this column' : `Select all ${label ?? ''}`.trim()}
      aria-label={`Select all ${label ?? 'cells'} in column`}
      onChange={() => onToggle(!allChecked)}
    />
  );
}
